import React from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import { defineMessages, FormattedMessage } from 'react-intl';

import Icon from '../Icon';
import './styles.css';

export const MAX_FILE_SIZE = 20971520; // 20MB = 20971520 bytes
export const DEFAULT_ALLOWED_TYPES = ['image/png', 'image/gif', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

export const messages = defineMessages({
  title: {
    id: 'drop-zone.title1',
    defaultMessage: 'Select an image to upload',
  },
  subtitle: {
    id: 'drop-zone.subtitle',
    defaultMessage: 'or drag it here',
  },
  uploadErrorTitle: {
    id: 'drop-zone.uploadErrorTitle',
    defaultMessage: 'Your image file may be corrupted',
  },
  uploadErrorMessage: {
    id: 'drop-zone.uploadErrorMessage',
    defaultMessage: 'Please try again or select another image.',
  },
  sizeErrorTitle: {
    id: 'drop-zone.sizeErrorTitle',
    defaultMessage: 'Invalid Image Size',
  },
  sizeErrorMessage: {
    id: 'drop-zone.sizeErrorMessage',
    defaultMessage: 'Images must have a file size less than 20MB.',
  },
  fileTypeErrorTitle: {
    id: 'drop-zone.fileTypeErrorTitle',
    defaultMessage: 'Invalid File Type',
  },
  fileTypeErrorMessage: {
    id: 'drop-zone.fileTypeErrorMessage',
    defaultMessage: 'Images must be GIF, SVG, JPG or PNG format.',
  },
  fileTypeErrorMessage2: {
    id: 'drop-zone.fileTypeErrorMessage2',
    defaultMessage: 'Images must be JPG or PNG format.',
  },
});

export const ErrorMessage = (props) => {
  const { errorTitle, errorMessage } = props;
  return (
    <div styleName="drop-zone-reject">
      <Icon name="times circle" size="large" styleName="drop-zone-error-icon" />
      <b><FormattedMessage {...errorTitle} />&nbsp;&nbsp;</b>
      <FormattedMessage {...errorMessage} />
    </div>
  );
};

ErrorMessage.propTypes = {
  errorTitle: PropTypes.object,
  errorMessage: PropTypes.object,
};

export function parseFileError({ corrupted, isDragReject, allowedTypes, rejectedFile }) {
  let errorTitle;
  let errorMessage;
  let error = false;

  if (corrupted) {
    errorTitle = messages.uploadErrorTitle;
    errorMessage = messages.uploadErrorMessage;
    error = true;
  } else if (isDragReject) {
    errorTitle = messages.fileTypeErrorTitle;
    errorMessage = messages[allowedTypes.length < DEFAULT_ALLOWED_TYPES.length ? 'fileTypeErrorMessage2' : 'fileTypeErrorMessage'];
    error = true;
  } else if (rejectedFile) {
    errorTitle = messages[rejectedFile.size > MAX_FILE_SIZE ? 'sizeErrorTitle' : 'fileTypeErrorTitle'];
    if (rejectedFile.size > MAX_FILE_SIZE) {
      errorMessage = messages.sizeErrorMessage;
    } else {
      errorMessage = messages[allowedTypes.length < DEFAULT_ALLOWED_TYPES.length ? 'fileTypeErrorMessage2' : 'fileTypeErrorMessage'];
    }
    error = true;
  }

  return {
    error,
    errorTitle,
    errorMessage,
  };
}

// https://react-dropzone.js.org/
const ImageDropZone = (props) => {
  const { corrupted, instructions1, instructions2, allowedTypes, ...rest } = props;
  return (
    <Dropzone maxSize={MAX_FILE_SIZE} accept={allowedTypes} {...rest}>
      {({ getRootProps, getInputProps, isDragActive, isDragReject, fileRejections }) => {
        const rejectedFile = fileRejections[0];
        const { error, errorTitle, errorMessage } = parseFileError({ corrupted, isDragReject, allowedTypes, rejectedFile });

        return (
          <div
            {...getRootProps()}
            styleName={`drop-zone-container ${isDragActive ? 'drop-zone-accept' : ''}`}
          >
            {error && (
              <ErrorMessage errorTitle={errorTitle} errorMessage={errorMessage} />
            )}
            <input {...getInputProps()} />
            <Icon name="image outline" color="teal" size="massive" />
            <h3 data-cy="image-drop-zone-title" styleName="drop-zone-title">
              <FormattedMessage {...messages.title} /><br />
              <span styleName="drop-zone-subtitle"><FormattedMessage {...messages.subtitle} /></span>
            </h3>
            {instructions1 && <p styleName="drop-zone-instructions"><FormattedMessage {...instructions1} /></p>}
            {instructions2 && <p styleName="drop-zone-instructions"><FormattedMessage {...instructions2} /></p>}
          </div>
        );
      }}
    </Dropzone>
  );
};

export default ImageDropZone;

ImageDropZone.propTypes = {
  instructions1: PropTypes.object,
  instructions2: PropTypes.object,
  corrupted: PropTypes.bool,
  allowedTypes: PropTypes.array,
};

ImageDropZone.defaultProps = {
  allowedTypes: DEFAULT_ALLOWED_TYPES,
};
